import { useState, type FormEvent, type ReactNode } from "react";

import { mensajeDe } from "@/lib/errors";

import { useChangePassword, useUsuario } from "./api";
import { LoginPage } from "./LoginPage";

/**
 * El portero del frontend: sin sesión, la aplicación no se dibuja.
 *
 * **Es comodidad, no seguridad.** Quien de verdad protege los datos es el backend, que
 * responde 401 a toda ruta de negocio; este componente solo evita que alguien vea un
 * armazón lleno de paneles vacíos y mensajes de error. Si esto fuera lo único, bastaría con
 * abrir las herramientas del navegador para saltárselo — y por eso no lo es.
 *
 * Mientras se pregunta quién eres no se pinta ni la aplicación ni el formulario: mostrar el
 * login medio segundo y quitarlo cuando la sesión resulta válida es peor que esperar.
 */
export function Guard({ children }: { children: ReactNode }) {
  const usuario = useUsuario();

  if (usuario.isPending) return <div aria-busy="true" className="min-h-dvh" />;
  if (usuario.isError) {
    return (
      <main className="grid min-h-dvh place-items-center px-4">
        <p role="alert" className="max-w-sm text-center text-sm text-ink-2">
          No se pudo hablar con el servidor. {(usuario.error as Error).message}
        </p>
      </main>
    );
  }
  if (!usuario.data) return <LoginPage />;
  if (usuario.data.must_change_password) return <ForcePasswordChange />;
  if (usuario.data.roles?.length === 0 && usuario.data.permissions?.length === 0) {
    return <main className="grid min-h-dvh place-items-center bg-canvas px-4"><div className="max-w-md rounded-xl border border-line bg-surface p-7 text-center"><h1 className="text-xl font-bold text-ink">Aún no tienes cargo</h1><p className="mt-3 text-sm text-ink-2">Pide al administrador que te asigne un cargo.</p></div></main>;
  }
  return <>{children}</>;
}

function ForcePasswordChange() {
  const change = useChangePassword();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (next !== confirm) { setError("Las dos contraseñas nuevas no coinciden."); return; }
    setError("");
    change.mutate({ current_password: current, new_password: next });
  }

  return <main className="grid min-h-dvh place-items-center bg-canvas px-4"><form onSubmit={submit} className="w-full max-w-md rounded-xl border border-line bg-surface p-7 text-ink shadow-card">
    <p className="mb-2 text-xs font-extrabold tracking-[.15em] text-workspace-accent uppercase">Acceso seguro</p>
    <h1 className="text-2xl font-extrabold">Cambia tu clave temporal</h1>
    <p className="mt-2 mb-6 text-sm text-ink-2">Para entrar al ERP, crea una contraseña que solo tú conozcas.</p>
    <label className="mb-4 grid gap-2 text-sm font-semibold">Clave temporal<input className="min-h-11 rounded-lg border border-line bg-surface-2 px-3 text-ink" type="password" autoComplete="current-password" value={current} onChange={event => setCurrent(event.target.value)} required /></label>
    <label className="mb-4 grid gap-2 text-sm font-semibold">Contraseña nueva<input className="min-h-11 rounded-lg border border-line bg-surface-2 px-3 text-ink" type="password" autoComplete="new-password" value={next} onChange={event => setNext(event.target.value)} required /></label>
    <label className="mb-4 grid gap-2 text-sm font-semibold">Repite la contraseña nueva<input className="min-h-11 rounded-lg border border-line bg-surface-2 px-3 text-ink" type="password" autoComplete="new-password" value={confirm} onChange={event => setConfirm(event.target.value)} required /></label>
    {error || change.error ? <p role="alert" className="mb-4 text-sm text-bad">{error || mensajeDe(change.error)}</p> : null}
    <button type="submit" disabled={change.isPending} className="min-h-11 w-full rounded-lg bg-workspace-accent px-4 font-bold text-workspace-accent-ink disabled:opacity-50">{change.isPending ? "Guardando…" : "Cambiar contraseña y entrar"}</button>
  </form></main>;
}
